// src/utils/ws.js
import store from '@/store'
import { showNotification } from '@/utils/notify'

/**
 * 特性：
 * - 单主连接（同域仅 1 个 Tab 维持 WebSocket）
 * - TTL 锁（异常退出锁残留，TTL 到期后其他 Tab 平滑接管）
 * - 不刷新页面接管
 * - 重连最多 5 次；超过后本 Tab 放弃，直到页面刷新
 * - “连接稳定期”后才清零重试计数，避免连上即断导致计数卡 1
 * - 并发连接保护：同一时刻只允许一次建连
 */

const WS_KEY = 'MY_APP_WS_CONNECTED'

// 锁/续租
const LOCK_TTL = 60 * 1000 // 60s
const RENEW_INTERVAL = 20 * 1000 // 20s
const STALE_CHECK_INTERVAL = 5 * 1000 // 5s
const TAKEOVER_JITTER_MIN = 50
const TAKEOVER_JITTER_MAX = 200

// WebSocket 心跳
const HEARTBEAT_TIME = 50 * 1000 // 50s

// 重连策略
const MAX_RETRIES = 5 // 最多 5 次
const RETRY_BASE_DELAY = 3000 // 3s → 6s → 12s → 24s → 48s 指数退避

// 稳定判定（连接成功后存活多久算“稳定”，再清零重试计数）
const STABLE_MS = 10 * 1000 // 10s

// 业务消息白名单
const ALLOW_TYPES = new Set(['notify', 'message', 'forceLogout', 'forceUpdate'])

function now() { return Date.now() }
function randInt(min, max) { return Math.floor(Math.random() * (max - min + 1)) + min }

class WS {
  constructor(url, user_name) {
    this.url = url
    this.user_name = user_name

    // 唯一 Tab 标识
    this.tabId = sessionStorage.getItem('TAB_ID') || `${now()}-${Math.random().toString(36).slice(2)}`
    sessionStorage.setItem('TAB_ID', this.tabId)

    // 连接/状态
    this.ws = null
    this.isMaster = false
    this.givenUp = false // 放弃标记：超过重连上限后，本 Tab 不再连接直到刷新
    this.connecting = false // 并发建连保护
    this.lastOpenAt = 0 // 最近一次 onopen 的时间
    this.stableTimer = null // 稳定期计时器

    // 定时器
    this.heartbeatTimer = null
    this.renewTimer = null
    this.staleCheckTimer = null
    this.retryTimer = null

    // 监听器
    this.storageListener = null

    // 重连计数
    this.retryCount = 0
  }

  /** 入口 */
  connect() {
    if (this.givenUp) return

    this._maybeBecomeMaster().then((isMaster) => {
      if (this.givenUp) return
      if (isMaster) {
        this.isMaster = true
        this._startRenewLease()
        this._createWS()
      } else {
        this.isMaster = false
        this._listenStorageForTakeover()
        this._startStaleChecker()
        console.log('%c[WebSocket] 本页面不连接，由其它 Tab 负责连接。', 'color: orange;')
      }
    })

    const release = () => this._releaseLeaseSafely()
    window.addEventListener('beforeunload', release)
    window.addEventListener('pagehide', release) // Safari/iOS
  }

  /** 创建/重连（仅主 Tab） */
  _createWS() {
    if (this.givenUp) return
    if (!this.isMaster) return
    if (this.ws && this.ws.readyState === WebSocket.OPEN) return
    if (this.connecting) return
    this.connecting = true

    // 超过上限则放弃
    if (this.retryCount >= MAX_RETRIES) {
      console.error(`[WebSocket] 已达最大重连次数(${MAX_RETRIES})，本标签页停止重连，等待刷新或他 Tab 接管`)
      this._giveUpAsMaster()
      this.connecting = false
      return
    }

    try {
      this.ws = new WebSocket(this.url)
    } catch (e) {
      this.connecting = false
      this._scheduleRetry()
      return
    }

    this.ws.onopen = () => {
      // 建连成功，不立刻清 0；先记录时间，等待稳定期
      this._clearRetryTimer()
      this.connecting = false
      this.lastOpenAt = Date.now()
      clearTimeout(this.stableTimer)
      this.stableTimer = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
          this.retryCount = 0 // ✅ 连接稳定后再清零
          // console.log('[WebSocket] 连接已稳定，重试计数清零')
        }
      }, STABLE_MS)

      const connectMsg = {
        type: 'connect',
        token: store.state.user.token,
        is_adx: 1
      }
      try { this.ws.send(JSON.stringify(connectMsg)) } catch { console.log('1') }
      this._startHeartbeat()
      console.log('%c[WebSocket] 已连接并发送 connect 包', 'color: green;')
    }

    this.ws.onmessage = (event) => {
      let data
      try { data = JSON.parse(event.data) } catch { return } // 非 JSON 忽略

      // 过滤心跳/握手
      if (data?.type === 'ping' || data?.type === 'pong' || data?.type === 'heart' || data?.type === 'connect_ack') return

      if (!ALLOW_TYPES.has(data?.type) && !data?.content) return

      if (data.content) {
        try { showNotification(data.title, data.content, data.url) } catch (e) { console.error('[WebSocket] showNotification error:', e) }
        try { window.dispatchEvent(new CustomEvent('notify:changed', { detail: data })) } catch { console.log('1') }
        try { localStorage.setItem('NOTIFY_CHANGED', String(now())) } catch { console.log('1') }
      }

      if (data.type === 'forceLogout') {
        // TODO: 这里交给你的业务逻辑处理，例如 store.dispatch('user/logout')
      }
    }

    this.ws.onclose = () => {
      this.connecting = false
      this._stopHeartbeat()
      clearTimeout(this.stableTimer)

      const lived = Date.now() - this.lastOpenAt
      // lived < STABLE_MS 时，不清零 retryCount，让其累加
      console.warn('[WebSocket] 连接断开，存活时长(ms)=', lived)
      this._scheduleRetry()
    }

    this.ws.onerror = (err) => {
      this.connecting = false
      console.error('[WebSocket] 错误:', err)
      // onclose 会触发 _scheduleRetry
    }
  }

  /** 计划下一次重连（指数退避 + 计数） */
  _scheduleRetry() {
    if (this.givenUp || !this.isMaster) return

    if (this.retryCount >= MAX_RETRIES) {
      console.error(`[WebSocket] 已达最大重连次数(${MAX_RETRIES})，停止重连`)
      this._giveUpAsMaster()
      return
    }

    this.retryCount += 1
    const delay = RETRY_BASE_DELAY * Math.pow(2, this.retryCount - 1) // 3,6,12,24,48
    console.warn(`[WebSocket] 第 ${this.retryCount}/${MAX_RETRIES} 次重连，${Math.round(delay / 1000)}s 后重试...`)
    this._clearRetryTimer()
    this.retryTimer = setTimeout(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) return
      this._createWS()
    }, delay)
  }
  _clearRetryTimer() {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer)
      this.retryTimer = null
    }
  }

  /** 超过重连上限后的放弃逻辑 */
  _giveUpAsMaster() {
    this.givenUp = true
    this._clearRetryTimer()
    this._stopHeartbeat()
    this._stopRenewLease()
    this._stopStaleChecker()
    this._unlistenStorage()
    try { this.ws && this.ws.close() } catch { console.log('1') }
    this.ws = null
    this._releaseLeaseSafely()
    this.isMaster = false
    console.warn('[WebSocket] 本标签页已放弃重连；刷新页面前将不再建立连接。')
  }

  /** 心跳 */
  _startHeartbeat() {
    if (this.heartbeatTimer) return
    this.heartbeatTimer = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const hb = { type: 'heart', is_adx: 1, account_type: 2, user_name: this.user_name }
        try { this.ws.send(JSON.stringify(hb)) } catch { console.log('1') }
      }
    }, HEARTBEAT_TIME)
  }
  _stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
  }

  /** 写消息（仅在 OPEN 下） */
  sendMessage(msg) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(typeof msg === 'string' ? msg : JSON.stringify(msg))
    } else {
      console.warn('[WebSocket] 未连接或非主 Tab，忽略发送')
    }
  }

  /** 手动关闭（一般不需要） */
  close() {
    try { this.ws && this.ws.close() } catch { console.log('1') }
    this._clearRetryTimer()
    this._stopHeartbeat()
    this._stopRenewLease()
    this._stopStaleChecker()
    this._unlistenStorage()
    this._releaseLeaseSafely()
    this.givenUp = true
  }

  // =========================
  //         锁机制
  // =========================

  async _maybeBecomeMaster() {
    if (this.givenUp) return false
    // 抖动，减少同刻争抢
    await new Promise(r => setTimeout(r, randInt(TAKEOVER_JITTER_MIN, TAKEOVER_JITTER_MAX)))
    const current = this._readLease()
    if (!current || this._isStale(current)) {
      return this._tryAcquireLease()
    }
    return false
  }

  _readLease() {
    try {
      const raw = localStorage.getItem(WS_KEY)
      if (!raw) return null
      const obj = JSON.parse(raw)
      if (!obj || typeof obj.ts !== 'number' || !obj.id) return null
      return obj
    } catch {
      return null
    }
  }

  _isStale(lease) {
    return !lease || (now() - lease.ts) > LOCK_TTL
  }

  _tryAcquireLease() {
    if (this.givenUp) return false
    const myLease = { id: this.tabId, ts: now() }
    try {
      localStorage.setItem(WS_KEY, JSON.stringify(myLease))
      const check = this._readLease()
      return check && check.id === this.tabId
    } catch {
      return false
    }
  }

  _startRenewLease() {
    if (this.renewTimer) return
    this.renewTimer = setInterval(() => {
      try {
        if (this.givenUp) return
        const curr = this._readLease()
        if (!curr) {
          // 丢失锁，尝试重新抢
          const ok = this._tryAcquireLease()
          this.isMaster = !!ok
        } else if (curr.id !== this.tabId) {
          // 被别人占锁 → 降级为副 Tab
          this.isMaster = false
          this._stopRenewLease()
          this._listenStorageForTakeover()
          this._startStaleChecker()
          try { this.ws && this.ws.close() } catch { console.log('1') }
          this.ws = null
          return
        } else {
          // 正常续租
          localStorage.setItem(WS_KEY, JSON.stringify({ id: this.tabId, ts: now() }))
        }
      } catch (e) {
        console.warn('[WebSocket] 续租失败：', e)
      }
    }, RENEW_INTERVAL)
  }
  _stopRenewLease() {
    if (this.renewTimer) {
      clearInterval(this.renewTimer)
      this.renewTimer = null
    }
  }

  _listenStorageForTakeover() {
    if (this.storageListener) return
    this.storageListener = (e) => {
      if (this.givenUp) return
      if (e.key !== WS_KEY) return
      const lease = this._readLease()
      if (!lease || this._isStale(lease)) {
        const ok = this._tryAcquireLease()
        if (ok) {
          console.log('%c[WebSocket] 接管成功（storage 事件）', 'color: blue;')
          this.isMaster = true
          this._stopStaleChecker()
          this._startRenewLease()
          this._unlistenStorage()
          this._createWS() // 平滑接管
        }
      }
    }
    window.addEventListener('storage', this.storageListener)
  }
  _unlistenStorage() {
    if (this.storageListener) {
      window.removeEventListener('storage', this.storageListener)
      this.storageListener = null
    }
  }

  _startStaleChecker() {
    if (this.staleCheckTimer) return
    this.staleCheckTimer = setInterval(() => {
      if (this.givenUp) return
      const lease = this._readLease()
      if (!lease || this._isStale(lease)) {
        const ok = this._tryAcquireLease()
        if (ok) {
          console.log('%c[WebSocket] 接管成功（定时检测）', 'color: blue;')
          this.isMaster = true
          this._stopStaleChecker()
          this._startRenewLease()
          this._unlistenStorage()
          this._createWS()
        }
      }
    }, STALE_CHECK_INTERVAL)
  }
  _stopStaleChecker() {
    if (this.staleCheckTimer) {
      clearInterval(this.staleCheckTimer)
      this.staleCheckTimer = null
    }
  }

  _releaseLeaseSafely() {
    try {
      const lease = this._readLease()
      if (lease && lease.id === this.tabId) {
        localStorage.removeItem(WS_KEY)
        // console.log('[WebSocket] 释放锁')
      }
    } catch { console.log('1') }
  }
}

// 全局单例
let wsInstance = null

export default function getWS(user_name) {
  if (!wsInstance) {
    wsInstance = new WS(`${process.env.VUE_APP_NOTIFYSOCKET_API_URL}`, user_name)
    wsInstance.connect()
  }
  return wsInstance
}
